import type { ArtworkDocument, DrawOperation, NormPoint, RegionId, ToolId } from './types';
import { History } from './history';
import { RegionHitTester } from './hitTest';
import { StrokeBuilder } from './stroke';
import { createOperation, type IdFactory } from './operations';
import { replay } from './replay';
import { TURTLE_REGIONS } from './geometry/turtle';
import { DEFAULT_COLOR, DEFAULT_TOOL, ERASER_SIZE, MARKER_SIZE } from './config';
import type { IArtworkRenderer, LiveStroke } from '../render/IArtworkRenderer';
import { SCHEMA_VERSION } from './version';

/**
 * ColoringEngine — the Phaser-free heart of the prototype.
 *
 * Responsibilities:
 *  - own the History (source of truth)
 *  - resolve which region a stroke is locked to (RegionHitTester)
 *  - build interpolated stroke operations (StrokeBuilder)
 *  - drive an IArtworkRenderer (paint/erase/undo/reset/replay)
 *  - expose a serializable ArtworkDocument for persistence
 *
 * It intentionally knows nothing about pointer events, the DOM, canvases or
 * Phaser. Adapters translate input/render into these method calls.
 */
export interface EngineDeps {
  renderer: IArtworkRenderer;
  idFactory?: IdFactory;
  now?: () => number;
}

export type ChangeReason = 'stroke' | 'erase' | 'undo' | 'reset' | 'load' | 'tool' | 'color';

export class ColoringEngine {
  private readonly history = new History();
  private readonly hitTester = new RegionHitTester(TURTLE_REGIONS);
  private readonly renderer: IArtworkRenderer;
  private readonly idFactory?: IdFactory;
  private readonly now: () => number;

  private selectedColor = DEFAULT_COLOR;
  private selectedTool: ToolId = DEFAULT_TOOL;

  // Active-stroke state. Only one stroke may be active at a time.
  private builder: StrokeBuilder | null = null;
  private lockedRegion: RegionId | null = null;

  private changeListeners = new Set<(reason: ChangeReason) => void>();

  constructor(deps: EngineDeps) {
    this.renderer = deps.renderer;
    this.idFactory = deps.idFactory;
    this.now = deps.now ?? (() => Date.now());
  }

  // --- state accessors -------------------------------------------------------

  isStroking(): boolean {
    return this.builder !== null;
  }
  getTool(): ToolId {
    return this.selectedTool;
  }
  getColor(): string {
    return this.selectedColor;
  }
  getActiveRegion(): RegionId | null {
    return this.lockedRegion;
  }
  operationCount(): number {
    return this.history.count();
  }
  canUndo(): boolean {
    return this.history.canUndo();
  }

  onChange(fn: (reason: ChangeReason) => void): () => void {
    this.changeListeners.add(fn);
    return () => this.changeListeners.delete(fn);
  }
  private emit(reason: ChangeReason): void {
    for (const fn of this.changeListeners) fn(reason);
  }

  setColor(color: string): void {
    this.selectedColor = color;
    this.emit('color');
  }
  setTool(tool: ToolId): void {
    this.selectedTool = tool;
    this.emit('tool');
  }

  private currentSize(): number {
    return this.selectedTool === 'eraser' ? ERASER_SIZE : MARKER_SIZE;
  }

  // --- stroke lifecycle ------------------------------------------------------

  /**
   * Begin a stroke at a normalized point. Returns the locked region, or null if
   * the point is outside every region (outside the turtle) — in which case NO
   * stroke starts. Also returns null (and ignores) if a stroke is already active
   * (guards against duplicate strokes from secondary touches).
   */
  beginStroke(p: NormPoint): RegionId | null {
    if (this.builder) return null; // a stroke is already active — ignore.
    const region = this.hitTester.hit(p);
    if (region === null) return null; // outside the turtle — reject.
    this.lockedRegion = region;
    this.builder = new StrokeBuilder();
    this.builder.addPoint(p);
    this.pushLive();
    return region;
  }

  /** Extend the active stroke. No-op if there is no active stroke. */
  extendStroke(p: NormPoint): void {
    if (!this.builder) return;
    this.builder.addPoint(p);
    this.pushLive();
  }

  private pushLive(): void {
    if (!this.builder || !this.lockedRegion) return;
    const live: LiveStroke = {
      type: this.selectedTool === 'eraser' ? 'erase' : 'paint',
      regionId: this.lockedRegion,
      color: this.selectedColor,
      size: this.currentSize(),
      points: this.builder.build(),
    };
    this.renderer.setLive(live);
  }

  /**
   * Commit the active stroke as an operation. Returns the committed operation,
   * or null if there was no active stroke or it had no usable points.
   */
  endStroke(): DrawOperation | null {
    if (!this.builder || !this.lockedRegion) {
      this.clearActive();
      return null;
    }
    const points = this.builder.build();
    const region = this.lockedRegion;
    const isErase = this.selectedTool === 'eraser';
    this.clearActive();
    this.renderer.setLive(null);

    if (points.length === 0) return null;

    const op = createOperation({
      seq: this.history.peekNextSeq(),
      type: isErase ? 'erase' : 'paint',
      regionId: region,
      brushId: 'marker',
      color: this.selectedColor,
      size: isErase ? ERASER_SIZE : MARKER_SIZE,
      points,
      idFactory: this.idFactory,
      now: this.now,
    });
    this.history.commit(op);
    this.renderer.apply(op);
    this.emit(isErase ? 'erase' : 'stroke');
    return op;
  }

  /** Abort the active stroke without committing (pointercancel / lost capture). */
  cancelStroke(): void {
    this.clearActive();
    this.renderer.setLive(null);
  }

  private clearActive(): void {
    this.builder = null;
    this.lockedRegion = null;
  }

  // --- history operations ----------------------------------------------------

  undo(): boolean {
    if (this.builder) return false; // don't undo mid-stroke
    const undone = this.history.undo();
    if (!undone) return false;
    replay(this.history.operations(), this.renderer);
    this.emit('undo');
    return true;
  }

  reset(): void {
    this.cancelStroke();
    this.history.reset();
    replay(this.history.operations(), this.renderer);
    this.emit('reset');
  }

  // --- persistence bridge ----------------------------------------------------

  toDocument(): ArtworkDocument {
    return {
      version: SCHEMA_VERSION,
      operations: this.history.operations(),
      nextSeq: this.history.peekNextSeq(),
      selectedColor: this.selectedColor,
      selectedTool: this.selectedTool,
      updatedAt: this.now(),
    };
  }

  /** Load a validated document into the engine and re-render from scratch. */
  loadDocument(doc: ArtworkDocument): void {
    this.cancelStroke();
    this.history.restore(doc.operations, doc.nextSeq);
    this.selectedColor = doc.selectedColor;
    this.selectedTool = doc.selectedTool;
    replay(this.history.operations(), this.renderer);
    this.emit('load');
  }
}
